import { useEffect, useState } from 'react';
import { BarChart } from '../components/BarChart';
import { PieChart } from '../components/PieChart';
import { StackedColumnChart } from '../components/StackedColumnChart';
import { StatsCard } from '../components/StatsCard';
import { apiClient } from '../lib/api-client';
import {
  getPostStatusColor,
  getReservedActionKindColor,
  RESERVED_ACTION_KIND_LABELS
} from '../utils/status-colors';

interface DailyByStatus {
  date: string;
  byStatus: { status: string; count: number }[];
}

interface DashboardStats {
  posts: {
    total: number;
    byStatus: { status: string; count: number }[];
    dailyScheduledPosts: DailyByStatus[];
  };
  users: {
    total: number;
    activeUsers: number;
    dailyRegistrations: { date: string; count: number }[];
  };
  // 予約アクション機能で追加されたフィールド。バックエンドのデプロイ前は
  // undefined になりうるため、利用側は必ず防御的に扱うこと。
  actions?: {
    total: number;
    byKind: { kind: string; count: number }[];
    byStatus: { status: string; count: number }[];
    dailyScheduledActions: DailyByStatus[];
  };
}

/**
 * 日別の予約投稿・予約アクションをステータス別に合算する。
 * 両者は同じ日付範囲・同じ書式で来る前提（バックエンド契約）。
 * actionsDaily が未定義の場合（バックエンド未対応時）は postsDaily をそのまま返す。
 */
function mergeDailyByStatus(
  postsDaily: DailyByStatus[],
  actionsDaily?: DailyByStatus[]
): DailyByStatus[] {
  if (!actionsDaily) {
    return postsDaily;
  }

  const actionsByDate = new Map(actionsDaily.map(day => [day.date, day.byStatus]));

  return postsDaily.map(day => {
    const counts = new Map<string, number>();
    day.byStatus.forEach(s => counts.set(s.status, (counts.get(s.status) ?? 0) + s.count));
    const actionStatuses = actionsByDate.get(day.date) ?? [];
    actionStatuses.forEach(s => counts.set(s.status, (counts.get(s.status) ?? 0) + s.count));

    return {
      date: day.date,
      byStatus: Array.from(counts.entries()).map(([status, count]) => ({ status, count }))
    };
  });
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await apiClient.getDashboardStats();
      setStats(data);
    } catch (_err) {
      console.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className='text-gray-900 dark:text-gray-100'>Loading...</div>;
  }

  return (
    <div>
      <h1 className='mb-6 text-3xl font-bold text-gray-800 dark:text-white'>Dashboard</h1>

      {/* 統計情報セクション */}
      {stats && (
        <div className='mb-6 space-y-6'>
          {/* サマリーカード */}
          <div className='grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-4'>
            <StatsCard
              title='Total Users'
              value={stats.users.total}
              subtitle={`${stats.users.activeUsers} active`}
              icon='users'
            />
            <StatsCard
              title='Total Posts'
              value={stats.posts.total}
              subtitle='Last 30 days'
              icon='calendar'
            />
            <StatsCard
              title='Pending Posts'
              value={stats.posts.byStatus.find(s => s.status === 'PENDING')?.count ?? 0}
              subtitle='Awaiting execution'
              icon='clock'
            />
            {stats.actions !== undefined && (
              <StatsCard
                title='Total Reserved Actions'
                value={stats.actions.total}
                subtitle='Repost / Delete · Last 30 days'
                icon='repeat'
              />
            )}
          </div>

          {/* グラフセクション */}
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            {/* 投稿ステータス分布（直近30日間） */}
            <div className='rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
              <h3 className='mb-4 text-lg font-semibold text-gray-700 dark:text-gray-200'>
                Posts by Status
                <span className='ml-2 text-sm font-normal text-gray-500 dark:text-gray-400'>
                  (Last 30 days)
                </span>
              </h3>
              <PieChart
                data={stats.posts.byStatus.map(s => ({
                  label: s.status,
                  value: s.count,
                  color: getPostStatusColor(s.status)
                }))}
                total={stats.posts.total}
              />
            </div>

            {/* 予約アクションのステータス別分布（直近30日間）
                「Posts by Status」に統合せず隣に並べたのは、投稿と予約アクション
                （リポスト/削除）ではステータスの意味・母数が異なり、
                1つの円グラフに混在させると誤読を招くため。
                stats.actions が未定義（バックエンド未対応）の場合はカードごと非表示にする。 */}
            {stats.actions !== undefined && (
              <div className='rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
                <h3 className='mb-4 text-lg font-semibold text-gray-700 dark:text-gray-200'>
                  Actions by Status
                  <span className='ml-2 text-sm font-normal text-gray-500 dark:text-gray-400'>
                    (Last 30 days)
                  </span>
                </h3>
                <PieChart
                  data={stats.actions.byStatus.map(s => ({
                    label: s.status,
                    value: s.count,
                    color: getPostStatusColor(s.status)
                  }))}
                  total={stats.actions.total}
                />
              </div>
            )}

            {/* 種別割合（予約投稿 / 予約リポスト / 予約削除）
                stats.actions が未定義の場合は予約投稿のみで100%表示する（防御的実装） */}
            <div className='rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
              <h3 className='mb-4 text-lg font-semibold text-gray-700 dark:text-gray-200'>
                Reservations by Kind
                <span className='ml-2 text-sm font-normal text-gray-500 dark:text-gray-400'>
                  (Last 30 days)
                </span>
              </h3>
              <PieChart
                data={[
                  {
                    label: RESERVED_ACTION_KIND_LABELS.POST,
                    value: stats.posts.total,
                    color: getReservedActionKindColor('POST')
                  },
                  ...(stats.actions?.byKind.map(k => ({
                    label: RESERVED_ACTION_KIND_LABELS[k.kind] ?? k.kind,
                    value: k.count,
                    color: getReservedActionKindColor(k.kind)
                  })) ?? [])
                ]}
                total={stats.posts.total + (stats.actions?.total ?? 0)}
              />
            </div>

            {/* 日別ユーザー登録数（直近14日間） */}
            <div className='rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
              <h3 className='mb-4 text-lg font-semibold text-gray-700 dark:text-gray-200'>
                Daily User Registrations
                <span className='ml-2 text-sm font-normal text-gray-500 dark:text-gray-400'>
                  (Last 14 days)
                </span>
              </h3>
              <BarChart
                data={stats.users.dailyRegistrations.map(r => ({
                  label: r.date,
                  value: r.count
                }))}
              />
            </div>
          </div>

          {/* 日別予約投稿数（ステータス別積み上げ）
              stats.actions がある場合は予約アクション分も同じ日付に合算して積み上げる */}
          <div className='rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
            <h3 className='mb-4 text-lg font-semibold text-gray-700 dark:text-gray-200'>
              {stats.actions !== undefined
                ? 'Daily Scheduled Posts & Actions by Status'
                : 'Daily Scheduled Posts by Status'}
              <span className='ml-2 text-sm font-normal text-gray-500 dark:text-gray-400'>
                (Past 15 days · Today · Next 15 days)
              </span>
            </h3>
            <StackedColumnChart
              data={mergeDailyByStatus(
                stats.posts.dailyScheduledPosts,
                stats.actions?.dailyScheduledActions
              )}
              getStatusColor={getPostStatusColor}
            />
          </div>
        </div>
      )}
    </div>
  );
}
